<template>
  <b-modal ref="modal" :title="sent ? 'Payment link sent' : 'Payment link'" size="lg" hide-footer centered>
    <p v-if="sent" class="text-muted mb-3">
      We emailed the link to the patient. They have <strong>10 minutes</strong> to pay on a short checkout page,
      then verify their email with a one-time code. After that the slot is released if unpaid.
    </p>

    <dl class="link-details">
      <dt>Patient</dt>
      <dd>{{ name || '—' }}</dd>
      <dt>Email</dt>
      <dd>{{ email || '—' }}</dd>
      <dt v-if="holdLabel">Hold ends</dt>
      <dd v-if="holdLabel" class="hold-countdown" :class="{ urgent: holdUrgent }">{{ holdLabel }}</dd>
      <dt>Payment link</dt>
      <dd>
        <b-input-group size="sm">
          <b-form-input ref="linkInput" :value="link" readonly class="link-input" @focus="$event.target.select()" />
          <b-input-group-append>
            <b-button variant="primary" @click="copyLink">
              <i :class="copied ? 'fa fa-check' : 'fa fa-copy'"></i> {{ copied ? 'Copied' : 'Copy link' }}
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </dd>
    </dl>

    <div class="actions">
      <b-button v-if="patientId && showPatientLink" variant="outline-primary" @click="go({ name: 'Patient', params: { patientId: String(patientId) } })">
        Open patient
      </b-button>
      <b-button variant="outline-primary" @click="goInvites">View consultation invites</b-button>
      <b-button variant="secondary" class="ml-auto" @click="hide">Close</b-button>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment'

/** Shows a consultation payment link with Copy link. Used right after sending and from the patient list. */
export default {
  name: 'PaymentLinkModal',
  data () {
    return {
      patientId: null,
      name: '',
      email: '',
      link: '',
      expiresAt: null,
      sent: false,
      showPatientLink: true,
      copied: false,
      holdLabel: '',
      holdUrgent: false,
      holdTimer: null
    }
  },
  beforeDestroy () {
    this.stopHoldTimer()
  },
  methods: {
    /** opts: { patientId, name, email, link, expiresAt, sent, showPatientLink } */
    show (opts) {
      this.stopHoldTimer()
      Object.assign(this, {
        showPatientLink: true,
        sent: false,
        expiresAt: null,
        holdLabel: '',
        holdUrgent: false
      }, opts, { copied: false })
      this.$refs.modal.show()
      this.startHoldTimer()
    },
    hide () {
      this.stopHoldTimer()
      this.$refs.modal.hide()
    },
    startHoldTimer () {
      this.stopHoldTimer()
      if (!this.expiresAt) {
        return
      }
      const tick = () => {
        const ms = moment(this.expiresAt).diff(moment())
        if (ms <= 0) {
          this.holdLabel = 'Expired — send a new link to hold another slot'
          this.holdUrgent = true
          this.stopHoldTimer()
          return
        }
        const total = Math.ceil(ms / 1000)
        const m = Math.floor(total / 60)
        const s = total % 60
        this.holdLabel = `in ${m}:${s < 10 ? '0' : ''}${s} (slot held until then)`
        this.holdUrgent = total <= 60
      }
      tick()
      this.holdTimer = setInterval(tick, 1000)
    },
    stopHoldTimer () {
      if (this.holdTimer) {
        clearInterval(this.holdTimer)
        this.holdTimer = null
      }
    },
    copyLink () {
      if (!this.link) return
      const done = () => {
        this.copied = true
        this.$awn.success('Link copied')
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.link).then(done).catch(() => this.copyFallback(done))
      } else {
        this.copyFallback(done)
      }
    },
    copyFallback (done) {
      const input = this.$refs.linkInput && this.$refs.linkInput.$el
      if (input) {
        input.select()
        if (document.execCommand('copy')) {
          done()
          return
        }
      }
      this.$awn.info('Select the link and copy it')
    },
    go (route) {
      this.hide()
      if (this.$route.name !== route.name || String(this.$route.params.patientId) !== String(this.patientId)) {
        this.$router.push(route)
      }
    },
    goInvites () {
      sessionStorage.setItem('patientsActiveTab', 'invites')
      this.hide()
      if (this.$route.name === 'Patients') {
        this.$emit('show-invites')
      } else {
        this.$router.push({ name: 'Patients' })
      }
    }
  }
}
</script>

<style scoped>
.link-details dt {
  font-weight: 600;
  margin-top: 10px;
}
.hold-countdown {
  font-weight: 600;
  color: #2f6f55;
}
.hold-countdown.urgent {
  color: #b3261e;
}
.link-details dd {
  margin-bottom: 4px;
}
.link-input {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8rem;
}
.actions {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
</style>
